import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import FooterMini from '../components/FooterMini';
import { Button } from '../components/ui/button';
import { useAuth } from '../auth/AuthContext';
import { useI18n } from '../i18n';
import Seo from '../seo/Seo';

export default function Signup() {
  const { lang } = useI18n();
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    // Legal gate — acceptance of /terms and /privacy must be explicit
    // and recorded with the registration. The HTML `required` attribute
    // on the checkbox already prevents submit, but we double-check
    // here in case JS tampers with the DOM.
    if (!acceptTerms) {
      setErr('Você precisa aceitar os Termos de Uso e a Política de Privacidade para criar a conta.');
      return;
    }
    setErr(''); setBusy(true);
    try {
      await register({ email, password, name, locale: lang });
      nav('/account', { replace: true });
    } catch (e) {
      setErr(e.message || 'Falha ao criar conta');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Seo titleKey="seoSignupTitle" path="/signup" noindex />
      <Header />
      <main className="flex-1 grid place-items-center px-3 sm:px-5 py-8 sm:py-10 blueprint-grid">
        <div className="w-full max-w-md bg-white rounded-xl border border-zinc-200 shadow-sm p-7">
          <h1 className="font-chunky text-2xl text-zinc-950 mb-1">Criar conta</h1>
          <p className="text-zinc-500 text-sm mb-6">Comece grátis. Faça upgrade para Pro a qualquer momento.</p>
          <form onSubmit={submit} className="space-y-3">
            <Field label="Nome">
              <input type="text" maxLength={80} value={name} onChange={(e) => setName(e.target.value)}
                     className="w-full px-3 py-2 rounded-md border border-zinc-200 outline-none focus:border-cyan-500 font-mono text-sm" />
            </Field>
            <Field label="E-mail">
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                     className="w-full px-3 py-2 rounded-md border border-zinc-200 outline-none focus:border-cyan-500 font-mono text-sm" />
            </Field>
            <Field label="Senha (mínimo 8 caracteres)">
              <input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
                     className="w-full px-3 py-2 rounded-md border border-zinc-200 outline-none focus:border-cyan-500 font-mono text-sm" />
            </Field>
            {/* Terms acceptance — explicit, required, and links to
                both /terms and /privacy. Capturing the consent checkbox
                value at signup is the legal anchor that lets us refuse
                refunds or invoke the chargeback ban policy later: the
                user has to actively tick the box, so they cannot claim
                they did not know the rules. */}
            <label className="flex gap-2 items-start text-[12px] text-zinc-600 leading-relaxed mt-1">
              <input
                type="checkbox"
                required
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                className="mt-0.5 shrink-0 accent-cyan-600"
              />
              <span>
                Li e aceito os{' '}
                <Link to="/terms" target="_blank" rel="noopener noreferrer"
                      className="text-cyan-700 hover:text-cyan-900 underline underline-offset-2">
                  Termos de Uso
                </Link>
                {' '}e a{' '}
                <Link to="/privacy" target="_blank" rel="noopener noreferrer"
                      className="text-cyan-700 hover:text-cyan-900 underline underline-offset-2">
                  Política de Privacidade
                </Link>
                , incluindo a política de reembolso e as regras de uso responsável de IA.
              </span>
            </label>
            {err && <div className="text-rose-700 bg-rose-50 border border-rose-200 rounded p-2 text-xs">{err}</div>}
            <Button disabled={busy || !acceptTerms} type="submit"
                    className="w-full bg-zinc-950 hover:bg-zinc-800 text-cyan-300 font-chunky uppercase tracking-wider disabled:opacity-50 disabled:cursor-not-allowed">
              {busy ? '...' : 'Criar conta'}
            </Button>
          </form>
          <div className="text-[12px] text-zinc-500 mt-4">
            Já tem conta? <Link to="/login" className="text-cyan-700 hover:underline">Entrar</Link>
          </div>
        </div>
      </main>
      <FooterMini />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="font-chunky uppercase tracking-wider text-[10px] text-zinc-600 mb-1">{label}</div>
      {children}
    </label>
  );
}
